import {announce} from '@github-ui/aria-live'
import {defaultBoardLabels} from '@github-ui/board'
import {SharedListLoadingSkeleton} from '@github-ui/shared-dashboard-lists/shared/SharedListLoadingSkeleton'
import {dashboardTypeLabels} from '@github-ui/shared-dashboard-sidebar/types/dashboard-types'
import {useEffect} from 'react'

import {BoardSurfaceSkeleton} from './board/generic/BoardSurface'
import type {Layout} from '../utils/saved-view-url-params'

/** How many placeholder rows a list draws, matching the issues dashboard. */
const LIST_ITEMS = 5

/** Read off the same labels the placeholder's own hidden text is built from. */
const LIST_LOADING = `Loading ${dashboardTypeLabels.issues}`

/**
 * The list placeholder, announced.
 *
 * `SharedListLoadingSkeleton` mounts hidden text and nothing else, which reaches only a
 * reader who goes looking for it. A layout is applied from a menu that keeps focus, so
 * the placeholder arrives without one and nothing else says the surface changed.
 * `BoardSkeleton` announces itself for the same reason.
 *
 * Announced here rather than inside `SharedListLoadingSkeleton`, which the issues and
 * pull request dashboards also draw. Those routes call `useScreenReaderAnnouncements`,
 * which says nothing while a request is out and announces the result count when it
 * lands. Announcing from the shared component would add a second announcement to every
 * one of them. This route calls nothing of the kind, so the list is otherwise silent.
 */
function ListLayoutSkeleton() {
  useEffect(() => {
    announce(LIST_LOADING)
  }, [])

  return <SharedListLoadingSkeleton dashboardType="issues" numItems={LIST_ITEMS} />
}

/**
 * The placeholder for a saved view whose layout is changing.
 *
 * A layout change is the one request where neither surface can draw its own placeholder:
 * the response in hand is the layout being left, and its results are the wrong shape for
 * the layout arriving. So the host draws this instead of either surface, and neither one
 * is handed results it cannot read.
 *
 * Shapeless on purpose. Nothing on screen is the layout being loaded, so there is no
 * column count or row count to copy, and both placeholders fall back to their own
 * defaults.
 */
export function SavedViewLayoutSkeleton({layout}: {layout: Layout}) {
  return layout === 'board' ? <BoardSurfaceSkeleton labels={defaultBoardLabels} /> : <ListLayoutSkeleton />
}
